// =============================================================================
// useTrabajadorConsentimiento
// Registra el consentimiento legal del trabajador al activar su cuenta
// desde el email de invitación o al recuperar contraseña si aún no
// ha aceptado los términos y condiciones de la plataforma.
// =============================================================================

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

const POLICY_VERSION = '1.0.0'; // Actualizar al cambiar términos

type ConsentimientoResult = {
  ok: boolean;
  error?: string;
};

export function useTrabajadorConsentimiento() {
  const [loading, setLoading] = useState(false);
  const [yaAceptado, setYaAceptado] = useState<boolean | null>(null);

  const comprobarConsentimiento = useCallback(async (): Promise<void> => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        console.warn('⚠️ No hay sesión activa');
        return;
      }

      const { id: id_usuario } = session.user;

      const { data: existing } = await supabase
        .from('trabajador_consentimientos')
        .select('id_trabajador_consentimiento')
        .eq('id_usuario', id_usuario)
        .eq('policy_version', POLICY_VERSION)
        .maybeSingle();

      setYaAceptado(!!existing);
    } catch (e) {
      console.error('❌ Error en comprobarConsentimiento:', e);
      setYaAceptado(false);
    }
  }, []);

  const registrarConsentimiento = useCallback(async (): Promise<ConsentimientoResult> => {
    setLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        return { ok: false, error: 'Sesión no válida' };
      }

      const { id: id_usuario, email } = session.user;

      // Intentar obtener la empresa del trabajador (puede ser null
      // si viene del flujo de recuperar contraseña sin perfil trabajador)
      const { data: perfil } = await supabase
        .from('usuarios_perfiles')
        .select('id_empresa')
        .eq('id_usuario', id_usuario)
        .eq('activo', true)
        .maybeSingle();

      const id_empresa = perfil?.id_empresa ?? null;

      // Comprobar si ya existe consentimiento para esta versión
      const { data: existing } = await supabase
        .from('trabajador_consentimientos')
        .select('id_trabajador_consentimiento')
        .eq('id_usuario', id_usuario)
        .eq('policy_version', POLICY_VERSION)
        .maybeSingle();

      if (existing) {
        setYaAceptado(true);
        return { ok: true };
      }

      const { error } = await supabase
        .from('trabajador_consentimientos')
        .insert({
          id_usuario,
          id_empresa,
          email: email ?? '',
          acepta_tratamiento: true,
          acepta_comunicaciones: false,
          policy_version: POLICY_VERSION,
        });

      if (error) return { ok: false, error: error.message };

      setYaAceptado(true);
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.message ?? 'Error desconocido' };
    } finally {
      setLoading(false);
    }
  }, []);

  return { registrarConsentimiento, comprobarConsentimiento, yaAceptado, loading };
}